<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { NButton, NInput, NPopconfirm, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import CvVersionList from './CvVersionList.vue'
import CvDocument from './CvDocument.vue'
import CvDocumentEditor from './CvDocumentEditor.vue'
import CvPreview from './CvPreview.vue'
import CvPublishPanel from './CvPublishPanel.vue'
import CvGenerationPanel from './CvGenerationPanel.vue'
import { useCvProfileStore, type CvProfile, type CvProfileFields } from '../stores/cv_profile'
import { useCvVersionsStore, type CvDocument as CvDocumentData } from '../stores/cv_versions'
import { useCvGenerationRunsStore } from '../stores/cv_generation_runs'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Admin-only Live CV page: the owner profile form (the contact block every
// rendered CV is built against, typed once and kept out of regeneration)
// and the generated versions. Generation is a queued job watched over
// ActionCable; a draft is hand-corrected in the structured editor,
// approved, and published here.
const store = useCvProfileStore()
const versions = useCvVersionsStore()
const runs = useCvGenerationRunsStore()
const message = useMessage()
const { formatAbsoluteDate } = useFormattedDate()
useDocumentTitle('Live CV')

interface Field {
  key: keyof CvProfileFields
  label: string
  placeholder: string
  hint?: string
}

const FIELDS: Field[] = [
  { key: 'name', label: 'Name', placeholder: 'Full name as it should appear on the CV' },
  { key: 'location', label: 'Location', placeholder: 'Oslo, Norway' },
  { key: 'email', label: 'Email', placeholder: 'you@example.com' },
  { key: 'linkedin_url', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/in/…' },
  { key: 'github_url', label: 'GitHub', placeholder: 'https://github.com/…' },
  {
    key: 'birth_date',
    label: 'Birth date',
    placeholder: 'YYYY-MM-DD',
    hint: 'Stored to compute your age. The date itself is never shown on the CV.',
  },
]

const draft = reactive<Record<keyof CvProfileFields, string>>({
  name: '',
  location: '',
  email: '',
  linkedin_url: '',
  github_url: '',
  birth_date: '',
})
const saving = ref(false)

function loadDraft(profile: CvProfile): void {
  for (const field of FIELDS) draft[field.key] = profile[field.key] ?? ''
}

watch(() => store.profile, loadDraft, { immediate: true })

onMounted(async () => {
  runs.subscribe()
  try {
    await Promise.all([store.fetchProfile(), versions.fetchVersions(), runs.fetchRuns()])
  } catch {
    // Each store's loadError surfaces in the template; nothing to do here.
  }
})

function toFields(): CvProfileFields {
  const fields = {} as CvProfileFields
  for (const field of FIELDS) fields[field.key] = draft[field.key].trim() || null
  return fields
}

async function save(): Promise<void> {
  saving.value = true
  try {
    await store.save(toFields())
    message.success('Profile saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save profile')
  } finally {
    saving.value = false
  }
}

// The editor works on a copy of the selected draft so an unsaved edit never
// leaks into the list; selecting another version discards it.
const editing = ref<CvDocumentData | null>(null)
const savingDraft = ref(false)
const approving = ref(false)

watch(
  () => versions.selected,
  (selected) => {
    // JSON round-trip: the document is plain JSON, and structuredClone rejects a reactive proxy.
    editing.value = selected?.status === 'draft' ? JSON.parse(JSON.stringify(selected.data)) : null
  },
  { immediate: true },
)

async function saveDraft(): Promise<void> {
  if (!versions.selected || !editing.value) return
  savingDraft.value = true
  try {
    await versions.updateDraft(versions.selected.id, editing.value)
    message.success('Draft saved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to save the draft')
  } finally {
    savingDraft.value = false
  }
}

async function approve(): Promise<void> {
  if (!versions.selected) return
  approving.value = true
  try {
    await versions.approve(versions.selected.id)
    message.success('Version approved')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to approve')
  } finally {
    approving.value = false
  }
}

const publishing = ref(false)
const hasApproved = computed(() => versions.versions.some((v) => v.status === 'approved'))

async function setPublished(published: boolean): Promise<void> {
  publishing.value = true
  try {
    await store.setPublished(published)
    message.success(published ? 'Public page is on' : 'Public page is off')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to change publishing')
  } finally {
    publishing.value = false
  }
}

async function generate(): Promise<void> {
  const inFlight = runs.active?.id
  try {
    const run = await runs.generate()
    message.success(run.id === inFlight ? 'A draft is already generating' : 'Draft queued')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to queue a draft')
  }
}
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-6 pb-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-2xl font-semibold text-white m-0">Live CV</h1>
        <p class="text-sm text-white/60 m-0">
          A CV generated from what the brain knows about you, reviewed and approved here before it goes public.
        </p>
      </header>

      <section class="flex flex-col gap-3">
        <h2 class="text-lg font-semibold text-white m-0">Contact block</h2>
        <p class="text-xs text-white/50 m-0">
          Enter it once; regenerating the CV from the brain never touches these fields.
        </p>

        <div v-if="store.loading" class="flex items-center justify-center py-12" data-testid="cv-profile-loading">
          <NSpin />
        </div>

        <div v-else-if="store.loadError" class="text-red-300 text-sm" data-testid="cv-profile-load-error">
          Failed to load the CV profile: {{ store.loadError }}
        </div>

        <form
          v-else
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-3"
          data-testid="cv-profile-form"
          @submit.prevent="save"
        >
          <label v-for="field in FIELDS" :key="field.key" class="flex flex-col gap-1 text-xs text-white/60">
            {{ field.label }}
            <NInput
              v-model:value="draft[field.key]"
              :placeholder="field.placeholder"
              :disabled="saving"
              :data-testid="`cv-profile-${field.key}`"
            />
            <span v-if="field.hint" class="text-white/40">{{ field.hint }}</span>
          </label>

          <div class="flex items-center gap-3 pt-1">
            <NButton type="primary" size="small" attr-type="submit" :loading="saving" data-testid="cv-profile-save">
              Save profile
            </NButton>
            <span v-if="store.profile.updated_at" class="text-xs text-white/40" data-testid="cv-profile-updated-at">
              Last saved {{ formatAbsoluteDate(store.profile.updated_at) }}
            </span>
          </div>
        </form>
      </section>

      <CvPublishPanel
        v-if="store.loaded && !store.loadError"
        :published="store.profile.published"
        :has-approved="hasApproved"
        :saving="publishing"
        @toggle="setPublished"
      />

      <section class="flex flex-col gap-3">
        <CvGenerationPanel
          :active="runs.active"
          :failed="runs.failed"
          :triggering="runs.triggering"
          @generate="generate"
        />

        <div v-if="versions.loadError" class="text-red-300 text-sm" data-testid="cv-versions-load-error">
          Failed to load CV versions: {{ versions.loadError }}
        </div>
        <p
          v-else-if="versions.loaded && versions.versions.length === 0"
          class="text-sm text-white/50 m-0"
          data-testid="cv-versions-empty"
        >
          No versions yet. Generate the first draft once the contact block is saved.
        </p>
        <CvVersionList
          v-else
          :versions="versions.versions"
          :selected-id="versions.selectedId"
          @select="versions.select"
        />

        <div
          v-if="versions.selected"
          class="rounded border border-white/10 bg-black/20 p-4 flex flex-col gap-4"
          data-testid="cv-selected"
        >
          <template v-if="editing">
            <CvDocumentEditor v-model="editing" />
            <div class="flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
              <NButton size="small" :loading="savingDraft" data-testid="cv-save-draft" @click="saveDraft">
                Save draft
              </NButton>
              <NPopconfirm positive-text="Approve" negative-text="Cancel" @positive-click="approve">
                <template #trigger>
                  <NButton type="primary" size="small" :loading="approving" data-testid="cv-approve">Approve</NButton>
                </template>
                Approve this version? It replaces the currently approved one. Unsaved edits are not included.
              </NPopconfirm>
            </div>
          </template>
          <CvDocument v-else :document="versions.selected.data" />
          <CvPreview :version-id="versions.selected.id" :is-draft="versions.selected.status === 'draft'" />
          <div class="border-t border-white/10 pt-3 text-xs text-white/50" data-testid="cv-selected-citations">
            <span class="text-white/40">Grounded on:</span>
            <span v-for="(c, i) in versions.selected.citations" :key="c.path"
              ><span v-if="i > 0">, </span>{{ c.title }}</span
            >
          </div>
        </div>
      </section>
    </section>
  </RouteShell>
</template>
